import { useEffect, useState } from "react";
import Lenis from "lenis";
import { api, mediaUrl } from "../lib/api";
import Seo from "../components/Seo";
import { LanguageProvider, setSiteOverrides } from "../lib/i18n";
import { LogoMark } from "../components/Logo";
import Navbar, { scrollToId } from "../components/site/Navbar";
import Hero from "../components/site/Hero";
import About from "../components/site/About";
import Villas from "../components/site/Villas";
import Dining from "../components/site/Dining";
import Experiences from "../components/site/Experiences";
import Offers from "../components/site/Offers";
import BoatSchedule from "../components/site/BoatSchedule";
import Gallery from "../components/site/Gallery";
import Reviews from "../components/site/Reviews";
import Blog from "../components/site/Blog";
import Contact from "../components/site/Contact";
import InstagramSection from "../components/site/InstagramSection";
import Footer from "../components/site/Footer";
import PromoPopup from "../components/site/PromoPopup";
import WhatsAppButton from "../components/site/WhatsAppButton";

export default function Home() {
  const [site, setSite] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
    window.__lenis = lenis;
    let raf;
    const loop = (time) => {
      lenis.raf(time);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
      window.__lenis = null;
    };
  }, []);

  useEffect(() => {
    api
      .get("/site")
      .then((r) => setSite(r.data))
      .catch(() => setError(true));
  }, []);

  // Deep-link support: when arriving from another page with a #section hash, scroll to it once content is ready
  useEffect(() => {
    if (!site || !window.location.hash) return;
    const id = window.location.hash.replace("#", "");
    const timer = setTimeout(() => scrollToId(id), 400);
    return () => clearTimeout(timer);
  }, [site]);

  useEffect(() => {
    const s = site?.settings;
    if (!s) return;
    if (s.favicon_url) {
      const link = document.querySelector("link[rel~='icon']");
      if (link) link.href = mediaUrl(s.favicon_url);
    }
    if (s.meta_description) {
      const m = document.querySelector("meta[name='description']");
      if (m) m.content = s.meta_description;
    }
    if (s.meta_keywords) {
      let m = document.querySelector("meta[name='keywords']");
      if (!m) {
        m = document.createElement("meta");
        m.name = "keywords";
        document.head.appendChild(m);
      }
      m.content = s.meta_keywords;
    }
    if (s.og_image) {
      let m = document.querySelector("meta[property='og:image']");
      if (!m) {
        m = document.createElement("meta");
        m.setAttribute("property", "og:image");
        document.head.appendChild(m);
      }
      m.content = mediaUrl(s.og_image);
    }
  }, [site]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FBF9F5] text-[#2C2825] font-sans">
        <p data-testid="site-error">Unable to load the site right now. Please refresh.</p>
      </div>
    );
  }

  if (!site) {
    return (
      <div data-testid="site-loader" className="min-h-screen flex flex-col items-center justify-center bg-[#1E1A17] gap-5">
        <LogoMark className="w-14 h-14 animate-pulse" color="#D8CBB8" />
        <span className="text-[11px] uppercase tracking-[0.4em] text-[#D8CBB8]/70 font-sans">
          Nusadira Lembongan
        </span>
      </div>
    );
  }

  const { settings, hero_slides, villas, dining, experiences, offers, gallery, posts, reviews } = site;
  setSiteOverrides(site.content || {});
  const hidden = new Set(
    String(settings?.hidden_sections || "").split(",").map((s) => s.trim()).filter(Boolean)
  );
  const igMatch = String(settings?.social_links || "").match(/https?:\/\/[^\s|]*instagram\.com\/[^\s|]*/);
  const instagramUrl = igMatch ? igMatch[0] : "";
  const shareImage =
    settings?.og_image ||
    (hero_slides || []).find((slide) => slide.active !== false)?.image_url ||
    (hero_slides || [])[0]?.image_url ||
    "";

  return (
    <LanguageProvider>
      <Seo
        title={settings?.meta_title || "Nusadira Lembongan Villa | Private Ocean Sanctuary, Bali"}
        description={settings?.meta_description || "Nusadira Lembongan Villa is a three-bedroom private villa with an ocean-view pool, quiet cliffside setting and island concierge on Nusa Lembongan, Bali."}
        keywords={settings?.meta_keywords || ""}
        image={shareImage ? mediaUrl(shareImage) : ""}
        imageAlt={settings?.og_image_alt || "Nusadira Lembongan Villa"}
      />
      <h1 className="sr-only">Nusadira Lembongan Villa — Private Ocean Sanctuary on Nusa Lembongan</h1>
      <div className="App relative overflow-x-clip">
        <div
          data-testid="site-logo-watermark"
          aria-hidden="true"
          className="pointer-events-none fixed inset-0 z-[5] overflow-hidden"
        >
          <LogoMark
            color="#9A7B66"
            className="absolute -right-40 top-36 w-[34rem] sm:w-[44rem] rotate-12 opacity-[0.025]"
          />
          <LogoMark
            color="#9A7B66"
            className="absolute -left-48 bottom-16 w-[28rem] sm:w-[36rem] -rotate-12 opacity-[0.018]"
          />
        </div>
        <Navbar
          bookingUrl={settings?.booking_engine_url}
          logoUrl={settings?.logo_url}
          logoLightUrl={settings?.logo_light_url}
          navMenu={settings?.nav_menu}
          hiddenSections={[...hidden]}
        />
        <Hero settings={settings} slides={hero_slides} />
        {!hidden.has("about") && <About gallery={gallery} />}
        {!hidden.has("villas") && (
          <Villas villas={villas} bookingUrl={settings?.booking_engine_url} factsheetUrl={settings?.factsheet_url} />
        )}
        {!hidden.has("dining") && <Dining items={dining} bookingUrl={settings?.booking_engine_url} />}
        {!hidden.has("experiences") && <Experiences experiences={experiences} bookingUrl={settings?.booking_engine_url} />}
        {!hidden.has("offers") && <Offers offers={offers} bookingUrl={settings?.booking_engine_url} />}
        {!hidden.has("schedule") && <BoatSchedule phone={settings?.contact_phone} settings={settings} />}
        {!hidden.has("gallery") && <Gallery gallery={gallery} />}
        {!hidden.has("reviews") && reviews.length > 0 && <Reviews reviews={reviews} />}
        {!hidden.has("blog") && <Blog posts={posts} />}
        {!hidden.has("contact") && <Contact settings={settings} />}
        <InstagramSection instagramUrl={instagramUrl} gallery={gallery} />
        <Footer settings={settings} />
        {!hidden.has("offers") && <PromoPopup settings={settings} offers={offers} popups={site.popups || []} />}
        <WhatsAppButton phone={settings?.contact_phone} />
      </div>
    </LanguageProvider>
  );
}
